import { getLineItemPaidPrice } from "@babackapp/calculations/get-line-item-paid-price";
import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import { useMemo } from "react";
import { useCurrentReturnRequest } from "../../utils/current-return-request-context";
import { getTotalDiscountToHandle } from "../../utils/get-total-discount";
import useLineItemsState from "./use-line-items-state-context";
import { useRefundLineItems } from "./use-refund-line-items-context";

type UseTotalDiscountProps = {
  withCharges?: boolean;
  withBonus?: boolean;
  chargeForRefund?: boolean;
  chargeForInitialShipping?: boolean;
  forceRefundInitialShipping?: boolean;
};

export const useTotalDiscount = ({
  withCharges = false,
  withBonus = true,
  chargeForRefund = false,
  chargeForInitialShipping = false,
  forceRefundInitialShipping = false,
}: UseTotalDiscountProps = {}) => {
  const currentReturnRequest = useCurrentReturnRequest();
  const { items: draftLineItems } = useRefundLineItems();
  const { lineItemsState } = useLineItemsState();

  const total = useMemo(() => {
    const totalCustomerPaidPriceDiscount = draftLineItems.reduce(
      (prev: number, lineItem: ReturnRequestLineItemDto) => {
        if (lineItem.resolution !== "discount") return prev;
        const lineItemSelectedQuantity =
          lineItemsState[lineItem.line_item_id]?.total.selectedQuantity ?? 0;

        const customerPaidPrice = getLineItemPaidPrice({ lineItem: lineItem });
        return prev + lineItemSelectedQuantity * customerPaidPrice;
      },
      0,
    );

    return getTotalDiscountToHandle(
      currentReturnRequest,
      totalCustomerPaidPriceDiscount,
      {
        withBonus,
        withCharges: withCharges,
        chargeForRefund: chargeForRefund,
        chargeForInitialShipping: chargeForInitialShipping,
        forceRefundInitialShipping: forceRefundInitialShipping,
      },
    );
  }, [
    currentReturnRequest,
    draftLineItems,
    lineItemsState,
    withBonus,
    withCharges,
    chargeForRefund,
    chargeForInitialShipping,
    forceRefundInitialShipping,
  ]);

  return total;
};
